import React from 'react'
import {
  Button,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { COLOURS, KIND } from './chipTypes'

const ReadOnlyField = ({ label, value }) => (
  <Stack gap={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || '—'}
    </Typography>
  </Stack>
)

// Editor for the currently selected chip. `selected` is
// { section, row, chip, kind } from useLoadListBuilder.
const PropertiesPanel = ({ selected, readOnly, onUpdate, onRemove }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: COLOURS.divider,
        borderRadius: '5px',
        p: 1.5,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} mb={1}>
        Item properties
      </Typography>

      {!selected ? (
        <Typography variant="body2" color="text.secondary">
          Select an item on the canvas to change its quantity or expiry rules.
        </Typography>
      ) : (
        <Stack gap={1.5}>
          <Typography variant="caption" color="text.secondary">
            In {selected.section.sectionName.trim() || 'an unnamed section'}
          </Typography>

          {selected.kind === KIND.CONSUMABLE ? (
            <>
              <TextField
                size="small"
                label="Name"
                value={selected.chip.name}
                required
                error={!selected.chip.name.trim()}
                helperText={
                  !selected.chip.name.trim() ? 'A name is required' : ''
                }
                disabled={readOnly}
                onChange={(e) => onUpdate({ name: e.target.value })}
              />
              <TextField
                size="small"
                label="Size / variant"
                value={selected.chip.size}
                disabled={readOnly}
                onChange={(e) => onUpdate({ size: e.target.value })}
              />
            </>
          ) : (
            <>
              <ReadOnlyField label="Category" value={selected.chip.category} />
              <ReadOnlyField
                label="Sub-category"
                value={
                  selected.kind === KIND.ASSET_WILDCARD
                    ? `Any ${selected.chip.category}`
                    : selected.chip.subCategory
                }
              />
              {selected.kind === KIND.ASSET_WILDCARD && (
                <Typography variant="caption" color="text.secondary">
                  A wildcard slot is satisfied by any asset in this category.
                </Typography>
              )}
            </>
          )}

          <TextField
            size="small"
            label="Quantity"
            type="number"
            value={selected.chip.quantity ?? 1}
            disabled={readOnly}
            inputProps={{ min: 1, step: 1 }}
            onChange={(e) => {
              const v = e.target.value
              if (v === '') return onUpdate({ quantity: '' })
              const n = Math.floor(Number(v))
              if (Number.isFinite(n)) onUpdate({ quantity: Math.max(1, n) })
            }}
            onBlur={() => {
              const n = Number(selected.chip.quantity)
              if (!Number.isFinite(n) || n < 1) onUpdate({ quantity: 1 })
            }}
          />

          {selected.kind === KIND.CONSUMABLE && (
            <Stack gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                Expiry
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                disabled={readOnly}
                value={selected.chip.noExpiry ? 'noExpiry' : 'dated'}
                onChange={(e, v) => {
                  if (v) onUpdate({ noExpiry: v === 'noExpiry' })
                }}
              >
                <ToggleButton value="dated">Dated</ToggleButton>
                <ToggleButton value="noExpiry">No expiry</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                {selected.chip.noExpiry
                  ? 'The checker only confirms the quantity present.'
                  : "The checker records each unit's expiry date during the check."}
              </Typography>
            </Stack>
          )}

          {!readOnly && (
            <Button
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={onRemove}
              sx={{ alignSelf: 'flex-start' }}
            >
              Remove from section
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  )
}

export default PropertiesPanel
